import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface FirmRow {
  id: string;
  name: string;
  contactEmail: string;
  seatCount: number;
  seatsUsed: number;
  termEndDate: any;
  active: boolean;
}

export function FirmsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [firms, setFirms] = useState<FirmRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    load();
  }, [user]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(query(collection(db, 'firms'), orderBy('name')));
      setFirms(snap.docs.map(d => {
        const f = d.data();
        return {
          id: d.id,
          name: f.name ?? '',
          contactEmail: f.contactEmail ?? '',
          seatCount: f.seatCount ?? 0,
          seatsUsed: f.seatsUsed ?? 0,
          termEndDate: f.termEndDate,
          active: f.active ?? false,
        };
      }));
    } catch (e: any) {
      setError(e.message || 'Failed to load firms.');
    } finally {
      setLoading(false);
    }
  };

  const totalActive = firms.filter(f => f.active).length;
  const totalSeats = firms.reduce((s, f) => s + f.seatCount, 0);
  const totalUsed = firms.reduce((s, f) => s + f.seatsUsed, 0);

  return (
    <Layout>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
        <div className="stat-card">
          <div className="stat-label">Total Outplacement Firms</div>
          <div className="stat-value">{loading ? '—' : firms.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value" style={{ color: 'var(--accent-text)' }}>{loading ? '—' : totalActive}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Seats Consumed</div>
          <div className="stat-value">{loading ? '—' : `${totalUsed} / ${totalSeats}`}</div>
        </div>
      </div>

      <div className="page-header">
        <h1 className="page-title">Outplacement</h1>
        <button className="btn-primary" onClick={() => navigate('/firms/new')}>
          + Add Outplacement Firm
        </button>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading">Loading…</div>
        ) : firms.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
            No outplacement firms yet. <button className="btn-primary" style={{ marginLeft: 12 }} onClick={() => navigate('/firms/new')}>Add the first one</button>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Firm</th>
                  <th>Contact</th>
                  <th>Seats</th>
                  <th>Term End</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {firms.map(f => (
                  <tr key={f.id}>
                    <td style={{ fontWeight: 500 }}>{f.name}</td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                      {f.contactEmail}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {f.seatsUsed} / {f.seatCount}
                      {f.seatCount > 0 && (
                        <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>
                          ({Math.round((f.seatsUsed / f.seatCount) * 100)}%)
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {fmtDate(f.termEndDate)}
                    </td>
                    <td>
                      <span className={`badge badge-${f.active ? 'active' : 'inactive'}`}>
                        {f.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
